/** @format */

import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { UtilityService, TaggingService, WINDOW } from '@shared/utils/services';
import { AuthenticationService } from '@shared/authentication/data-access';
import { ExclusiveContentService } from '@shared/data-access/services';
import { map } from 'rxjs/operators';

@Component({
    selector: 'home-story-v4-hero',
    templateUrl: './story-v4.component.html',
    styleUrls: ['./story-v4.component.scss'],
    standalone: false
})
export class StoryV4Component implements OnInit {
	@Input() data: any;
	@Input() addlData: any;

	public copyBoxStyles: any;
	public copyBoxOrientation: string;
	public copyBoxTextColor: string;
	public copyBoxBackground: string;
	public copyBoxClasses;
	public targetService: any;
	public displayCTAS = false;
	public displayBadge = false;
	public headlineClasses: string;
	public subheadlineClasses: string;
	public storyId: string;

	public displayConnectSidePanel = false;
	public connectSidePanelData;
	public connectSidePanelCtaType = 'signIn';

	constructor(
		private taggingService: TaggingService,
		private utilityService: UtilityService,
		private authService: AuthenticationService,
		private exclusiveContentService: ExclusiveContentService,
		@Inject(PLATFORM_ID) private platformID,
		@Inject(WINDOW) private window
	) {
		// determines if a cta link should open a new window based upon the url
		this.targetService = this.utilityService.target;
	}

	ngOnInit() {
		this.copyBoxStyles = {
			'text-align': this.data[0].textAlignment ? this.data[0].textAlignment : 'center'
		};
		// adds classes for desktop and mobile copy box
		this.copyBoxOrientation = `${this.data[0].textBoxPositionV2} mobile-${this.data[0].mobileTextBoxPosition}`;

		this.copyBoxTextColor = `${this.data[0].textColor}-text`;
		this.copyBoxBackground = this.data[0].textBlockBackground ? 'background' : 'no-background';
		this.copyBoxClasses = `${this.copyBoxTextColor} ${this.copyBoxBackground}`;

		// only apply the copyBoxMaxWidth class when floated text box layouts are selected
		if (
			(this.data[0].copyBoxMaxWidth && this.data[0].textBoxPositionV2 === 'float-center') ||
			this.data[0].textBoxPositionV2 === 'float-left' ||
			this.data[0].textBoxPositionV2 === 'float-right'
		) {
			this.copyBoxClasses += ` ${this.data[0].copyBoxMaxWidth}`;
		}

		if (this.data[0].headline) {
			// if no headline font size specified, default to display-md
			const headlineFontSize = this.data[0].headlineFontSizeV2
				? this.data[0].headlineFontSizeV2
				: 'display-md';
			// if no mobile headline font size specified, default to display-md
			const mobileHeadlineFontSize = this.data[0].mobileHeadlineFontSizeV2
				? this.data[0].mobileHeadlineFontSizeV2
				: 'mobile-display-md';
			this.headlineClasses = `${headlineFontSize} ${mobileHeadlineFontSize}`;
		}

		// assign storyId for navigation component linking
		const idStart = 'StoryV4';
		// eslint-disable-next-line no-useless-escape
		this.storyId =
			idStart +
			(this.data[0].headline
				? this.data[0].headline
				: this.data[0].subhead
				? this.data[0].subhead
				: this.addlData.tagSectionNum
			).replace(
				/™|_|®|©|%|&reg;|&copy;|&#8482;|&#174;|&#169;|&trade;|'|"|\.|,|;|\?|!|&|\(|\)|\^|\s+/g,
				''
			);

		if (this.data[0].subhead) {
			// if no headline font size specified, default to display-md
			const subheadFontSize = this.data[0].subheadlineFontSizeV2
				? this.data[0].subheadlineFontSizeV2
				: 'heading-md';
			// if no mobile headline font size specified, default to display-md
			const mobileSubheadlineFontSize = this.data[0].mobileSubheadlineFontSizeV2
				? this.data[0].mobileSubheadlineFontSizeV2
				: 'mobile-heading-md';
			this.subheadlineClasses = `${subheadFontSize} ${mobileSubheadlineFontSize}`;
		}

		if (
			this.data[0].connectSidePanel &&
			!this.utilityService.isObjectInArrayEmpty(this.data[0].connectSidePanel)
		) {
			this.displayConnectSidePanel = true;
			this.connectSidePanelData = this.data[0].connectSidePanel[0];
			if (isPlatformBrowser(this.platformID)) {
				//checking if a user is logged in
				this.authService.getIsAuthenticated$().subscribe((isAuthenticated) => {
					if (isAuthenticated) {
						this.connectSidePanelCtaType = 'connectAccounts';
						//checking if a user is Nike Connected
						if (this.window.party) {
							this.exclusiveContentService
								.isUserConnectedToSpecificParty('nike_connected')
								.pipe(
									map((isConnected) => {
										if (isConnected) {
											this.displayConnectSidePanel = false;
											this.displayCTAS =
												this.data[0].cta &&
												!this.utilityService.isObjectInArrayEmpty(
													this.data[0].cta
												);
										}
									})
								)
								.subscribe();
						} else {
							window.document.addEventListener('readyToParty', () => {
								this.exclusiveContentService
									.isUserConnectedToSpecificParty('nike_connected')
									.pipe(
										map((isConnected) => {
											if (isConnected) {
												this.displayConnectSidePanel = false;
												this.displayCTAS =
													this.data[0].cta &&
													!this.utilityService.isObjectInArrayEmpty(
														this.data[0].cta
													);
											}
										})
									)
									.subscribe();
							});
						}
					}
				});
			}
		}
		// determine if ctas object is empty
		// eslint-disable-next-line sonarjs/no-collapsible-if
		if (!this.connectSidePanelData) {
			if (this.data[0].cta && !this.utilityService.isObjectInArrayEmpty(this.data[0].cta)) {
				this.displayCTAS = true;
			}
		}
		// determine if badge object is empty
		if (this.data[0].badge && !this.utilityService.isObjectInArrayEmpty(this.data[0].badge)) {
			this.displayBadge = true;
		}
	}

	createTag(ctaIndex: number, ctaText: string, altText: string) {
		return this.taggingService.createTag(
			this.addlData.tagSectionNum,
			this.addlData.widgetTypeTag,
			0,
			ctaIndex,
			ctaText,
			altText,
			null,
			false,
			'hp'
		);
	}
}
